import type { CSSProperties } from "react";
import type { OrgDashboardData } from "../../../contexts/dashboard-context/OrgDashboardContext";
import { formatAdserverType } from "../../../utils";
import type { DataTableColumn } from "../Helpers/DataTable";

export const ORGANIZATION_DASHBOARD_BASE = "/organization-dashboard";

export const ORGANIZATION_DASHBOARD_PATHS = {
  federationDetails: `${ORGANIZATION_DASHBOARD_BASE}/federation-details`,
  organizationDetails: `${ORGANIZATION_DASHBOARD_BASE}/organization-details`,
  adserverDetails: `${ORGANIZATION_DASHBOARD_BASE}/adserver-details`,
} as const;

export type OrganizationDetailsVariant =
  | "federation"
  | "organization"
  | "adserver";

type SelectOption = { label: string; value: string };

export type DetailsFilterDefinition = {
  id: string;
  selectName: string;
  apiKey: string;
  disabled?: boolean;
  style?: CSSProperties;
  preserveOptionCase?: boolean;
  getOptions: (dashboardData: OrgDashboardData | null) => SelectOption[];
};

export type OrganizationDetailsPageConfig = {
  variant: OrganizationDetailsVariant;
  path: string;
  pageTitle: string;
  sectionTitle: string;
  tableTitle: string;
  filters: DetailsFilterDefinition[];
};

const allEventTypes = (dashboardData: OrgDashboardData | null): SelectOption[] =>
  Array.from(
    new Set((dashboardData?.eventType ?? []).map((item) => item.event_type)),
  ).map((option) => ({ label: option, value: option }));

const federationSelectOptions = (
  dashboardData: OrgDashboardData | null,
): SelectOption[] =>
  Array.from(
    new Map(
      (dashboardData?.federationMonth ?? []).map((item) => [
        item.federation_id,
        {
          value: item.federation_id,
          label: item.federation_name || item.federation_id,
        },
      ]),
    ).values(),
  );

const organizationSelectOptions = (
  _dashboardData: OrgDashboardData | null,
): SelectOption[] => [];

const adserverTypeOptions = (
  dashboardData: OrgDashboardData | null,
): SelectOption[] =>
  Array.from(
    new Set(
      (dashboardData?.adserverMonth ?? []).map(
        (item) => item.destination_adserver_type,
      ),
    ),
  ).map((option) => ({
    label: formatAdserverType(option),
    value: option,
  }));

export const withAll = (options: SelectOption[]): SelectOption[] => [
  { label: "All", value: "ALL" },
  ...options,
];

const eventTypeFilter = (id = "event_type"): DetailsFilterDefinition => ({
  id,
  selectName: "Event Type",
  apiKey: "event_type",
  getOptions: (dashboardData) => withAll(allEventTypes(dashboardData)),
});

const federationFilter: DetailsFilterDefinition = {
  id: "federation_id",
  selectName: "Federations",
  apiKey: "federation_id",
  getOptions: (dashboardData) =>
    withAll(federationSelectOptions(dashboardData)),
};

const organizationFilter: DetailsFilterDefinition = {
  id: "organization_id",
  selectName: "Organizations",
  apiKey: "organization_id",
  getOptions: (dashboardData) =>
    withAll(organizationSelectOptions(dashboardData)),
};

const adserverTypeFilter = (selectName: string): DetailsFilterDefinition => ({
  id: "destination_adserver_type",
  selectName,
  apiKey: "destination_adserver_type",
  preserveOptionCase: true,
  getOptions: (dashboardData) => withAll(adserverTypeOptions(dashboardData)),
});

/** Month = aggregated rows; day = daily breakdown when a table month pill is selected. */
export type DetailsTableTimeGranularity = "month" | "day";

const formatEventType = (val: string | number) =>
  String(val)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()
  ).replace("Marked Engaged", "Marked/Engaged");

const formatCount = (val: string | number) => {
  return Number(val).toLocaleString("en-US");
};

const getMetricColumns = (
  variant: OrganizationDetailsVariant,
): DataTableColumn[] => [
  { label: "Event Type", key: "eventType", formatter: formatEventType },
  { label: "Audience Volume", key: "totalCount", formatter: formatCount },
  {
    label: variant === "adserver" ? "Activation Count" : "Queries Executed",
    key: "queryCount",
    formatter: formatCount,
  },
];

const DETAILS_ENTITY_COLUMN: Record<OrganizationDetailsVariant, DataTableColumn> =
  {
    federation: { label: "Federation Name", key: "entityId" },
    organization: { label: "Organization Name", key: "entityId" },
    adserver: {
      label: "Destination Ad Server",
      key: "adserverType",
      formatter: (v) => formatAdserverType(v),
    },
  };

const DETAILS_TIME_COLUMN: Record<
  DetailsTableTimeGranularity,
  DataTableColumn
> = {
  month: { label: "Month", key: "timePeriodMonth" },
  day: { label: "Date", key: "date" },
};

export function getDetailsPageTableColumns(
  variant: OrganizationDetailsVariant,
  timeGranularity: DetailsTableTimeGranularity,
): DataTableColumn[] {
  if (variant === "adserver") {
    // Time column becomes the group header; Event Type is always Audience Transferred
    return [
      DETAILS_ENTITY_COLUMN[variant],
      { label: "Audience Volume", key: "totalCount", formatter: formatCount },
      { label: "Activation Count", key: "queryCount", formatter: formatCount },
    ];
  }
  return [
    DETAILS_ENTITY_COLUMN[variant],
    DETAILS_TIME_COLUMN[timeGranularity],
    ...getMetricColumns(variant),
  ];
}

export function getAdserverTableGroupKey(
  timeGranularity: DetailsTableTimeGranularity,
): string {
  return timeGranularity === "day" ? "date" : "timePeriodMonth";
}

export const FEDERATION_DETAILS_CONFIG: OrganizationDetailsPageConfig = {
  variant: "federation",
  path: ORGANIZATION_DASHBOARD_PATHS.federationDetails,
  pageTitle: "Organization Dashboard - Detailed Page",
  sectionTitle: "Federation Wise Summary",
  tableTitle: "Federation Summary Table",
  filters: [federationFilter, adserverTypeFilter("Adserver Type")],
};

export const ORGANIZATION_DETAILS_CONFIG: OrganizationDetailsPageConfig = {
  variant: "organization",
  path: ORGANIZATION_DASHBOARD_PATHS.organizationDetails,
  pageTitle: "Organization Dashboard - Detailed Page",
  sectionTitle: "Organization Summary",
  tableTitle: "Organization Summary",
  filters: [eventTypeFilter(), organizationFilter],
};

export const ADSERVER_DETAILS_CONFIG: OrganizationDetailsPageConfig = {
  variant: "adserver",
  path: ORGANIZATION_DASHBOARD_PATHS.adserverDetails,
  pageTitle: "Organization Dashboard - Detailed Page",
  sectionTitle: "Ad Server Summary",
  tableTitle: "Ad Server Summary",
  filters: [adserverTypeFilter("Destination ad server")],
};

const CONFIGS: OrganizationDetailsPageConfig[] = [
  FEDERATION_DETAILS_CONFIG,
  ORGANIZATION_DETAILS_CONFIG,
  ADSERVER_DETAILS_CONFIG,
];

export function getOrganizationDetailsConfig(
  pathname: string,
): OrganizationDetailsPageConfig {
  return CONFIGS.find((c) => c.path === pathname) ?? FEDERATION_DETAILS_CONFIG;
}

const RBAC_EXCLUDED_FILTER_IDS = new Set(["event_type", "federation_id"]);

export function getOrganizationDetailsFilters(
  pageConfig: OrganizationDetailsPageConfig,
  isAdmin: boolean,
): DetailsFilterDefinition[] {
  if (isAdmin) {
    return pageConfig.filters;
  }
  return pageConfig.filters.filter(
    (filter) => !RBAC_EXCLUDED_FILTER_IDS.has(filter.id),
  );
}

export function initialFilterValuesForFilters(
  filters: DetailsFilterDefinition[],
): Record<string, string> {
  const filterRecord: Record<string, string> = {};
  filters.forEach((currentFilter) => {
    filterRecord[currentFilter.id] = currentFilter.disabled ? "na" : "";
  });
  return filterRecord;
}

export function initialFilterValuesForConfig(
  pageConfig: OrganizationDetailsPageConfig,
): Record<string, string> {
  return initialFilterValuesForFilters(pageConfig.filters);
}

export function buildDetailsApiQueryParams(
  filters: DetailsFilterDefinition[],
  filterValues: Record<string, string>,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const currentFilter of filters) {
    if (currentFilter.disabled) continue;
    const value = filterValues[currentFilter.id];
    if (!value || value === "ALL") continue;
    params[currentFilter.apiKey] = value;
  }
  return params;
}
